/**
 * The rooms as separate chunks (planning-app.md §4.5: ≤ 200 KB of JavaScript before the first
 * screen). App.tsx wraps these in React.lazy; main.tsx calls `preloadRoute` with the first URL so
 * the room's chunk downloads alongside `/api/me` instead of after it.
 */
import { preloadNamespace, type UiLocale } from '../i18n';
import { SCOPE_NAMESPACES } from './namespaces';

// The entries must agree with SCOPE_ENTRIES (src/pages/namespaces.ts); `npm run i18n:check` compares them.
export const loaders = {
  signIn: () => import('./SignInPage').then((m) => ({ default: m.SignInPage })),
  pulse: () => import('./PulsePage').then((m) => ({ default: m.PulsePage })),
  decision: () => import('./decisions/DecisionPage').then((m) => ({ default: m.DecisionPage })),
  session: () => import('./sessions/SessionPage').then((m) => ({ default: m.SessionPage })),
  // The track page's Decisions tab and its Sessions view (pages/track/DecideTab.tsx, v3.8b): boundaries inside the track room.
  decideList: () => import('./track/DecisionList').then((m) => ({ default: m.DecisionList })),
  sessionsList: () => import('./track/TrackSessions').then((m) => ({ default: m.TrackSessions })),
  status: () => import('./StatusPage').then((m) => ({ default: m.StatusPage })),
  workspace: () => import('./workspace/WorkspacePage').then((m) => ({ default: m.WorkspacePage })),
  thread: () => import('./workspace/ThreadPage').then((m) => ({ default: m.ThreadPage })),
  // The one chat shell (v3.12, IA §5): the thread page imports it statically; the track, story, wireframe and Build run pages mount it as a nested boundary (pages/chat/LazyChat.tsx).
  chat: () => import('./chat/ChatMount').then((m) => ({ default: m.ChatMount })),
  // AI Assist (v3.15a, PA64): the panel behind the top bar's button, the chat chunk's catalog, loaded on open (components/assist/AssistDrawer.tsx).
  assist: () => import('./chat/AssistPanel').then((m) => ({ default: m.AssistPanel })),
  you: () => import('./YouPage').then((m) => ({ default: m.YouPage })),
  // Activity (§8 *Activity*, v3.54): the maintainers' page, its own chunk and catalog, linked from You › Team.
  activity: () => import('./activity/ActivityPage').then((m) => ({ default: m.ActivityPage })),
  // The desk dispersed (v3.8c, IA §1.5): a gate page under Plan › Gates, a finding under the Build console.
  gate: () => import('./verification/GatePage').then((m) => ({ default: m.GatePage })),
  finding: () => import('./verification/FindingPage').then((m) => ({ default: m.FindingPage })),
  plan: () => import('./plan/PlanPage').then((m) => ({ default: m.PlanPage })),
  planChange: () => import('./plan/PlanChangePage').then((m) => ({ default: m.PlanChangePage })),
  package: () => import('./plan/PackagePage').then((m) => ({ default: m.PackagePage })),
  units: () => import('./track/UnitsPage').then((m) => ({ default: m.UnitsPage })),
  unit: () => import('./track/UnitPage').then((m) => ({ default: m.UnitPage })),
  // User Stories (IA §3, v3.9a): the list, a story's page, and the list as the track page's Stories tab (pages/track/StoriesTab.tsx).
  stories: () => import('./stories/StoriesPage').then((m) => ({ default: m.StoriesPage })),
  story: () => import('./stories/StoryPage').then((m) => ({ default: m.StoryPage })),
  storiesList: () => import('./stories/StoriesPage').then((m) => ({ default: m.StoriesList })),
  reportAuthorize: () => import('./ReportAuthorizePage').then((m) => ({ default: m.ReportAuthorizePage })),
  docs: () => import('./docs/DocsPage').then((m) => ({ default: m.DocsPage })),
  doc: () => import('./docs/DocPage').then((m) => ({ default: m.DocPage })),
  translationQueue: () => import('./docs/TranslationQueuePage').then((m) => ({ default: m.TranslationQueuePage })),
  wireframes: () => import('./wireframes/WireframesPage').then((m) => ({ default: m.WireframesPage })),
  wireframe: () => import('./wireframes/WireframePage').then((m) => ({ default: m.WireframePage })),
  wireframeStale: () => import('./wireframes/StaleBatchPage').then((m) => ({ default: m.StaleBatchPage })),
  build: () => import('./build/BuildPage').then((m) => ({ default: m.BuildPage })),
  // The calendar (IA §6.8, v3.10): its own chunk under the plan namespace, so the board's code stays out of the phone's week agenda and vice versa.
  calendar: () => import('./plan/CalendarPage').then((m) => ({ default: m.CalendarPage })),
  buildRun: () => import('./build/BuildRunPage').then((m) => ({ default: m.BuildRunPage })),
};

const ROUTE_CHUNKS: [RegExp, keyof typeof loaders][] = [
  [/^\/?$/, 'pulse'],
  [/^\/sign-in\/?$/, 'signIn'],
  [/^\/decisions\/[^/]+/, 'decision'],
  [/^\/sessions\/[^/]+/, 'session'],
  [/^\/status\//, 'status'],
  [/^\/threads\/[^/]+/, 'thread'],
  [/^\/threads\/?$/, 'workspace'],
  [/^\/you\/?$/, 'you'],
  [/^\/activity\/?$/, 'activity'],
  [/^\/findings\/[^/]+/, 'finding'],
  [/^\/plan\/gates\/[^/]+/, 'gate'],
  [/^\/plan\/changes\/[^/]+/, 'planChange'],
  [/^\/tracks\/[^/]+/, 'unit'],
  [/^\/tracks\/?$/, 'units'],
  [/^\/stories\/[^/]+/, 'story'],
  [/^\/stories\/?$/, 'stories'],
  [/^\/plan\/calendar\/?$/, 'calendar'],
  [/^\/plan(\/gates)?\/?$/, 'plan'],
  [/^\/packages\/[^/]+/, 'package'],
  [/^\/report\/authorize/, 'reportAuthorize'],
  [/^\/docs\/.+/, 'doc'],
  [/^\/docs\/?$/, 'docs'],
  [/^\/translations\/[^/]+/, 'translationQueue'],
  [/^\/wireframes\/stale\/?$/, 'wireframeStale'],
  [/^\/wireframes\/[^/]+/, 'wireframe'],
  [/^\/wireframes\/?$/, 'wireframes'],
  [/^\/build\/runs\/[^/]+/, 'buildRun'],
  [/^\/build(\/findings)?\/?$/, 'build'],
];

export function preloadRoute(pathname: string, locale?: UiLocale): void {
  const hit = ROUTE_CHUNKS.find(([re]) => re.test(pathname));
  if (!hit) return;
  void loaders[hit[1]]().catch(() => undefined);
  // The room's messages (src/pages/namespaces.ts) download alongside its code.
  if (locale) for (const ns of [SCOPE_NAMESPACES[hit[1]]].flat()) void preloadNamespace(locale, ns).catch(() => undefined);
}
